import React, { useState } from 'react';
import { BygrModule, BygrProject, LicenseStatus, ProjectLicenseDto, SingleBygrModule } from '@/types';
import { useAsyncState } from '@/utils/hooks';
import { getProjectLicenseStatus, getProjectLicenses, setProjectLicenses } from '@/services/licenses';
import { Toast, Input, LoadingIndicator, ValidationForm } from '@ygtechnologies/bygr-frontend-component-library';
import { startCase } from 'lodash-es';
import theme from './ProjectLicenses.module.scss';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import { FlatCard } from '@/components/FlatCard';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';

export interface ProjectLicensesProps {
    project: BygrProject;
}

const modules = [BygrModule.ChangeManagement, BygrModule.Aftermarket, BygrModule.QualityManagement].map(
    (x) => BygrModule[x] as SingleBygrModule
);

interface ProjectLicenseRowProps {
    license: ProjectLicenseDto;
    licenseStatus: LicenseStatus;
    onChange(newValue: ProjectLicenseDto): void;
}

const ProjectLicenseRow: React.FC<ProjectLicenseRowProps> = ({ license, licenseStatus, onChange }) => {
    const handleAllowedUnitsChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const value = e.target.value === '' ? null : +e.target.value;
        onChange({ ...license, allowedUnits: value });
    };
    const handleNoteChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        onChange({ ...license, note: e.target.value });
    };

    return (
        <>
            <span>{startCase(license.module)}</span>
            <span className={theme.unitsUsed}>{licenseStatus.actualUnits}</span>
            <Input
                min={0}
                value={license?.allowedUnits ?? ''}
                type="number"
                onChange={handleAllowedUnitsChange}
                placeholder="Unlimited"
                className="form-control"
            />
            <Input
                placeholder="Note"
                value={license?.note ?? ''}
                className="form-control"
                type="text"
                maxLength={2000}
                onChange={handleNoteChange}
            />
        </>
    );
};

export const ProjectLicensesUnits: React.FC<ProjectLicensesProps> = ({ project }) => {
    const [licenses, setLicenses] = useAsyncState(() => getProjectLicenses(project.id), [project.id], null);
    const [licensesStatus] = useAsyncState(() => getProjectLicenseStatus(project.id), [project.id], null);
    const [saving, setSaving] = useState(false);

    if (!licenses || !licensesStatus) {
        return <LoadingIndicator />;
    }

    const handleSave = async () => {
        setSaving(true);
        try {
            await setProjectLicenses(project.id, licenses);
            Toast.success('Saved!');
        } finally {
            setSaving(false);
        }
    };

    const renderRow = (module: SingleBygrModule) => {
        const license = licenses.find((x) => x.module === module)!;
        const licenseStatus = licensesStatus.find((x) => x.module === module)!;

        return (
            <ProjectLicenseRow
                key={module}
                onChange={(newValue) => setLicenses(arrayReplace(licenses, license, newValue))}
                license={license}
                licenseStatus={licenseStatus}
            />
        );
    };

    return (
        <FlatCard title="Unit licenses">
            <ValidationForm onSubmit={handleSave}>
                <div className={theme.licenseGrid}>
                    <span className={theme.columnHeader}>Module</span>
                    <span className={theme.columnHeader}>Units used</span>
                    <span className={theme.columnHeader}>Licenses</span>
                    <span className={theme.columnHeader}>Note</span>
                    {modules.map((x) => renderRow(x))}
                </div>

                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="primary" type="submit" loading={saving}>
                            Save
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ValidationForm>
        </FlatCard>
    );
};
